import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Button, Input, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string;
  name: string;
  setName: (v: string) => void;
  clientName: string;
  setClientName: (v: string) => void;
  customDomain?: string;
  setCustomDomain: (v: string) => void;
  projectType?: number;
  promptVersion?: number | null;
  projectId?: string;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  name,
  setName,
  clientName,
  setClientName,
  customDomain,
  setCustomDomain,
  projectType,
  promptVersion,
  projectId,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainStatus, setDomainStatus] = useState<'idle' | 'available' | 'unavailable'>('idle');
  const [domainMsg, setDomainMsg] = useState('');
  const [initialDomain, setInitialDomain] = useState<string | undefined>('');

  useEffect(() => {
    if (isOpen) {
      setInitialDomain(customDomain);
      setDomainStatus('idle');
      setDomainMsg('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const normalizedCurrent = customDomain?.trim() || '';
  const normalizedInitial = initialDomain?.trim() || '';
  const isInitialDomain = normalizedCurrent === normalizedInitial;

  const isSubmitDisabled = loading || !name.trim() || (!isInitialDomain && !!normalizedCurrent && domainStatus !== 'available');

  const handleDomainCheck = async () => {
    if (!customDomain?.trim()) return;
    setCheckingDomain(true);
    setDomainStatus('idle');
    setDomainMsg('');
    try {
      const res = await api.get(`/projects/check-domain?domain=${customDomain.trim()}${projectId ? `&project_id=${projectId}` : ''}`);
      if (res.data.available) {
        setDomainStatus('available');
        setDomainMsg('Domain is available!');
      } else {
        setDomainStatus('unavailable');
        setDomainMsg(res.data.reason || 'Domain is unavailable');
      }
    } catch (err: any) {
      setDomainStatus('unavailable');
      setDomainMsg('Error checking domain');
    } finally {
      setCheckingDomain(false);
    }
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    setDomainStatus('idle');
    setDomainMsg('');
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Edit Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              <Input
                id="project-name-edit"
                name="project-name-edit"
                label="Project Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
                placeholder="Enter project name"
                className="w-full"
              />
              <Input
                id="client-name-edit"
                name="client-name-edit"
                label="Client Name"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="Enter client name"
                className="w-full"
              />
              
              {projectType === 1 && (promptVersion || 0) >= 2 && (
                <div className="flex-1 min-w-[280px]">
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Custom Domain</Text>
                  <div className="flex items-center gap-2">
                    <Input
                      id="custom-domain-edit"
                      name="custom-domain-edit"
                      value={customDomain || ''}
                      onChange={handleDomainChange}
                      placeholder="e.g. my-app"
                      className="flex-1"
                    />
                    <Text variant="text-sm" color="tertiary">.apps.bluegenai.com</Text>
                  </div>
                  {normalizedCurrent && (
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        size="small"
                        onClick={handleDomainCheck}
                        disabled={checkingDomain || domainStatus === 'available' || isInitialDomain}
                        label={
                          isInitialDomain ? "Current Domain" :
                          checkingDomain ? "Checking..." : 
                          domainStatus === 'available' ? "Available" : "Check Availability"
                        }
                      />
                      {domainMsg && !isInitialDomain && (
                        <Text variant="text-sm" color={domainStatus === 'available' ? 'success' : 'error'}>
                          {domainMsg}
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && <Text variant="text-sm" color="error" className="w-full">{error}</Text>}
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Saving...' : 'Save Changes'}
              disabled={isSubmitDisabled}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

